import { GridValidRowModel } from "@mui/x-data-grid";
import { SweepDestStatus } from "@wallet-manager/node-types/dist/src/postgres/const";
import { useState } from "react";
import { OpDelete, OpEdit, OpSwitch, OpView } from "src/assets/icons";
import { Box } from "src/components/MuiGenerals";
import OpIconButton from "src/components/OpIconButton";
import { Params } from "src/features/common/types/GridRenderCellParams";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { usePermission, useTranslation } from "src/hooks";
import { FeatureCodes } from "src/utils/constant";
import { GetListRes } from "../types/GetListRes";
import useDeleteGroupDialog from "./useDeleteGroupDialog";
import useEditGroupDialog from "./useEditGroupDialog";
import useSwitchGroupDialog from "./useSwitchGroupDialog";
import useViewGroupDialog from "./useViewGroupDialog";

const editPermission =
  FeatureCodes.assetManagement.ContractSweepRecipentSetting
    .ContractSweepRecipentGroup.edit;
const deletePermission =
  FeatureCodes.assetManagement.ContractSweepRecipentSetting
    .ContractSweepRecipentGroup.delete;
const viewPermission =
  FeatureCodes.assetManagement.ContractSweepRecipentSetting
    .ContractSweepRecipentGroup.view;
const switchPermission =
  FeatureCodes.assetManagement.ContractSweepRecipentSetting
    .ContractSweepRecipentGroup.switchGroup;

const useOperationCell = () => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t } = useTranslation(translatePrefix);
  const { hasPermission } = usePermission();

  //state of dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewGroupDialogOpen, setIsViewGroupDialogOpen] = useState(false);
  const [isSwitchGroupDialogOpen, setIsSwitchGroupDialogOpen] = useState(false);

  //state of selected data
  const [selectedRow, setSelectedRow] = useState<
    GridValidRowModel & { rawData: GetListRes }
  >();

  //edit dialog hook
  const { renderEditGroupDialog } = useEditGroupDialog({
    isOpen: isEditDialogOpen,
    onClose: () => {
      setIsEditDialogOpen(false);
      setSelectedRow(undefined);
    },
    selectedRow: selectedRow,
  });

  //delete dialog hook
  const { renderDeleteDialog } = useDeleteGroupDialog({
    isOpen: isDeleteDialogOpen,
    selectedRow: selectedRow,
    onClose: () => {
      setIsDeleteDialogOpen(false);
      setSelectedRow(undefined);
    },
  });

  //view group dialog hook
  const { renderViewGroupDialog } = useViewGroupDialog({
    isOpen: isViewGroupDialogOpen,
    onClose: () => {
      setIsViewGroupDialogOpen(false);
      setSelectedRow(undefined);
    },
    viewGroupData: {
      chainType: selectedRow?.rawData?.chain_type,
      chainId: selectedRow?.rawData?.chain_id,
      assetName: selectedRow?.rawData?.asset_name,
      groupId: selectedRow?.rawData?.id,
      groupSequence: selectedRow?.rawData?.group_seq,
      groupName: selectedRow?.rawData?.group_name,
      groupType: selectedRow?.rawData?.group_type,
      maximumAmountLimit: selectedRow?.rawData?.max_amount_limit,
      rotateAmountLimit: selectedRow?.rawData?.rotate_group_amount_limit,
    },
  });

  //switch Group Dialog
  const { renderSwitchGroupDialog } = useSwitchGroupDialog({
    isOpen: isSwitchGroupDialogOpen,
    selectedRow: selectedRow,
    onClose: () => {
      setIsSwitchGroupDialogOpen(false);
      setSelectedRow(undefined);
    },
  });

  const renderOperationCell = (params: Params<GetListRes>) => {
    const onEditClick = () => {
      setIsEditDialogOpen(true);
      setSelectedRow(params.row);
    };
    const onDeletClick = () => {
      setIsDeleteDialogOpen(true);
      setSelectedRow(params.row);
    };
    const onViewGroupClick = () => {
      setIsViewGroupDialogOpen(true);
      setSelectedRow(params.row);
    };
    const onSwitchDialogClick = () => {
      setIsSwitchGroupDialogOpen(true);
      setSelectedRow(params.row);
    };
    const getSwitchGroupDisabled = () => {
      if (params?.row?.rawData.is_active) {
        return true;
      }
      if (params?.row?.rawData.status === SweepDestStatus.Disable) {
        return true;
      }
      if (!params?.row?.rawData.is_valid) {
        return true;
      }
      return false;
    };
    return (
      <Box display={"flex"} flexDirection={"row"} gap={1}>
        {hasPermission(editPermission) && (
          <OpIconButton title={t("edit")} url={OpEdit} onClick={onEditClick} />
        )}
        {hasPermission(deletePermission) && (
          <OpIconButton
            title={t("delete")}
            url={OpDelete}
            onClick={onDeletClick}
          />
        )}
        {hasPermission(viewPermission) && (
          <OpIconButton
            title={t("view_group")}
            url={OpView}
            onClick={onViewGroupClick}
          />
        )}
        {hasPermission(switchPermission) && (
          <OpIconButton
            title={t("switch_group")}
            url={OpSwitch}
            onClick={onSwitchDialogClick}
            isDisabled={getSwitchGroupDisabled()}
          />
        )}
      </Box>
    );
  };

  const renderDialogInOperationCell = () => (
    <>
      {renderEditGroupDialog()}
      {renderDeleteDialog()}
      {renderViewGroupDialog()}
      {renderSwitchGroupDialog()}
    </>
  );

  return {
    renderOperationCell,
    renderDialogInOperationCell,
  };
};
export default useOperationCell;
